import {toLower} from "lodash"

export type FileMetadata = {
	name: string
	size: number
}

export enum FileType {
	MP3 = "MP3",
	MP4 = "MP4",
	M4A = "M4A",
	JPEG = "JPEG",
	JPG = "JPG",
	PNG = "PNG",
	WAV = "WAV",
	PDF = "PDF",
	DOCX = "DOCX",
}

export const convertFileExtensionToFileType = (extension?: string): FileType | undefined => {
	switch (toLower(extension)) {
		case "mp3":
			return FileType.MP3
		case "mp4":
			return FileType.MP4
		case "m4a":
			return FileType.M4A
		case "jpeg":
			return FileType.JPEG
		case "jpg":
			return FileType.JPG
		case "png":
			return FileType.PNG
		case "wav":
			return FileType.WAV
		case "pdf":
			return FileType.PDF
		case "docx":
			return FileType.DOCX
		default:
			return undefined
	}
}

export function fileReader(file: File) {
	return new Promise((resolve) => {
		const reader = new FileReader()
		reader.onload = () => {
			resolve(reader.result)
		}

		reader.readAsBinaryString(file)
	})
}

export function openLinkNewTab(link: string) {
	const a = document.createElement("a")
	document.body.appendChild(a)
	a.target = "_blank"
	a.rel = "noopener noreferrer"
	a.style.display = "none"
	a.href = link
	a.click()
	a.remove()
}

const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
	"application/msword": "doc",
	"application/pdf": "pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
	"audio/m4a": "m4a",
	"audio/mp3": "mp3",
	"audio/mp4": "m4a",
	"audio/mpeg": "mp3",
	"audio/x-m4a": "m4a",
	"audio/wav": "wav",
	"audio/x-wav": "wav",
	"image/bmp": "bmp",
	"image/gif": "gif",
	"image/jpeg": "jpg",
	"image/jpg": "jpg",
	"image/png": "png",
	"image/tiff": "tiff",
	"image/webp": "webp",
	"video/mp4": "mp4",
}

export function fileExtensionFromContentType(contentType: string): string {
	const base = (contentType.split(";")[0] ?? "").trim().toLowerCase()
	if (!base || base === "application/octet-stream") {
		return "bin"
	}
	if (Object.prototype.hasOwnProperty.call(MIME_TYPE_TO_EXTENSION, base)) {
		return MIME_TYPE_TO_EXTENSION[base]
	}
	return "bin"
}

export async function downloadFromUrl(
	url: string,
	filename: string,
	contentTypeFallback: string,
	signal?: AbortSignal,
	fetchOptions: RequestInit = {}
): Promise<boolean> {
	try {
		const response = await fetch(url, {
			credentials: "omit",
			cache: "no-store",
			...fetchOptions,
			...(signal ? {signal} : {}),
		})
		if (!response.ok) {
			return false
		}
		const blob = await response.blob()
		if (signal?.aborted) {
			return false
		}
		downloadFile(filename, blob, contentTypeFallback)
		return true
	} catch {
		return false
	}
}

export function downloadFile(filename: string, data: unknown, fileType: string) {
	const fileData =
		data instanceof Blob && Boolean(data.type && data.type !== "application/octet-stream")
			? data
			: new Blob([data as BlobPart], {type: fileType})
	const a = document.createElement("a")
	document.body.appendChild(a)
	a.style.display = "none"
	a.href = URL.createObjectURL(fileData)
	a.download = filename
	a.click()
	URL.revokeObjectURL(a.href)
	a.remove()
}

export const formatFileSize = (sizeInBytes: number): string => {
	const kilobyte = 1000
	const megabyte = kilobyte * 1000
	const gigabyte = megabyte * 1000

	if (sizeInBytes < kilobyte) {
		return `${sizeInBytes} B`
	} else if (sizeInBytes < megabyte) {
		return `${(sizeInBytes / kilobyte).toFixed(2)} KB`
	} else if (sizeInBytes < gigabyte) {
		return `${(sizeInBytes / megabyte).toFixed(2)} MB`
	} else {
		return `${(sizeInBytes / gigabyte).toFixed(2)} GB`
	}
}

export const extractExtensionAndFileName = (name?: string): {extension?: string; nameWithoutExtension: string} => {
	if (!name) {
		return {nameWithoutExtension: ""}
	}
	const lastDotIndex = name.lastIndexOf(".")
	if (lastDotIndex !== -1) {
		const extension = name.slice(lastDotIndex + 1)
		const nameWithoutExtension = name.slice(0, lastDotIndex)
		return {extension, nameWithoutExtension}
	} else {
		return {nameWithoutExtension: name}
	}
}

export const getMimeTypeByFileName = (fileName?: string): string | undefined => {
	const fileExtension = extractExtensionAndFileName(fileName).extension
	switch (toLower(fileExtension)) {
		case "jpeg":
		case "jpg":
			return "image/jpeg"
		case "png":
			return "image/png"
		case "pdf":
			return "application/pdf"
		case "docx":
			return "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
		case "mp3":
			return "audio/mpeg"
		case "mp4":
			return "video/mp4"
		case "m4a":
			return "audio/mp4"
		case "wav":
			return "audio/wav"
		default:
			return undefined
	}
}

export const base64ToBlob = (base64File: string): {blob: Blob; contentType: string} => {
	// Extract the base64 content without the data URL prefix if it exists
	const base64Data = base64File.includes("base64,") ? base64File.split("base64,")[1] : base64File

	// Convert base64 to binary
	const binaryString = atob(base64Data)
	const bytes = new Uint8Array(binaryString.length)
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i)
	}

	// Create a Blob from the binary data
	const blob = new Blob([bytes])

	// Detect content type from base64 string
	let contentType = "application/octet-stream"
	if (base64File.includes("data:")) {
		contentType = base64File.split(";")[0].split(":")[1]
	}

	return {blob, contentType}
}

export const readFileAsBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => {
			resolve(reader.result as string)
		}
		reader.onerror = (error) => {
			reject(error)
		}
		reader.readAsDataURL(file)
	})
}
